import React, {useEffect, useRef, useState} from 'react';
import {useHistory} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck, faCircleInfo, faCreditCard, faTriangleExclamation, faWallet} from '@fortawesome/free-solid-svg-icons';
import {FormattedMessage} from 'react-intl';
import API from '../services/API';
import Utils from '../services/Utils';
import Error from '../services/Error';
import Countries from './Countries';

// Mêmes listes que le formulaire d'inscription (pages/subscription/Signup.js) : garder les deux alignées.
const FREE_FEATURES = ['FEAT_BOOKINGS_5', 'FEAT_VEHICLE_1', 'FEAT_MODULE_1', 'FEAT_DRIVER_PAYMENT'];
const PAID_FEATURES = ['FEAT_BOOKINGS_UNLIMITED', 'FEAT_VEHICLES_UNLIMITED', 'FEAT_MODULE_UNLIMITED', 'FEAT_ONLINE_PAYMENT', 'FEAT_CALENDAR_SYNC'];

// Contenu de gestion d'abonnement. Rendu uniquement par SubscriptionModal (il n'y a pas de page dédiée) ;
// reçoit `plan` en prop plutôt que via usePlan() pour éviter l'import circulaire PlanContext → modale → panel.
// `plan` = la valeur du contexte plan (passée en prop pour éviter un import circulaire avec PlanContext).
export default ({intl, plan, limitReason}) => {
  const history = useHistory();
  const [prices, setPrices] = useState(null);
  const [loading, setLoading] = useState(false);
  // Toute bascule de formule engage de l'argent (changer d'intervalle réinitialise l'ancre de facturation
  // Stripe : une facture part immédiatement) ou fait perdre des fonctionnalités. On demande donc une
  // confirmation en place — pas de window.confirm, bloquant et hors du style de l'app.
  // Valeurs : null | 'FREE' | 'MONTHLY' | 'ANNUAL'.
  const [confirmTarget, setConfirmTarget] = useState(null);

  // Formulaire des informations légales de facturation, affiché avant Stripe quand le compte ne les
  // porte pas encore. On n'y demande que ce que Stripe ne sait pas obtenir à temps : la raison sociale
  // (le Checkout ne collecte que le nom du porteur de la carte) et le pays / n° de TVA, qui figent le
  // taux avant l'ouverture de la session. L'adresse, c'est le Checkout qui l'exige.
  // Valeurs : null | 'MONTHLY' | 'ANNUAL' (la formule visée).
  const [billingTarget, setBillingTarget] = useState(null);
  // `null` tant que rien n'a été saisi : le formulaire se lit alors sur le plan (qui arrive après le
  // premier rendu), ce qui évite un useEffect de préremplissage et sa fenêtre de valeurs vides.
  const [billing, setBilling] = useState(null);
  const billingRef = useRef(null);
  useEffect(() => {
    if (billingTarget && billingRef.current) {
      billingRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  }, [billingTarget]);

  // La confirmation est rendue après les cartes de formule : sur mobile, empilées, elles la repoussent
  // hors écran (on croit que le bouton ne réagit pas). Dès qu'elle apparaît, on la fait défiler à l'écran.
  const confirmRef = useRef(null);
  useEffect(() => {
    if (confirmTarget && confirmRef.current) {
      confirmRef.current.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  }, [confirmTarget]);

  useEffect(() => {
    API.getSubscriptionPrices().then(r => {
      if (r && r.data && r.data.success === true) {
        setPrices(r.data.data);
      }
    }).catch(() => {});
  }, []);

  const p = plan || {};
  const fmt = new Intl.NumberFormat(intl.locale || 'fr', {style: 'currency', currency: (prices && prices.currency) || 'EUR'});
  const monthly = prices ? parseFloat(prices.monthly) : null;
  const annual = prices ? parseFloat(prices.annual) : null;
  const annualPerMonth = annual != null ? annual / 12 : null;
  const annualSave = (monthly != null && annual != null) ? (monthly * 12 - annual) : null;

  // Le backend ne connaît que FREE / PAID (tout le gating du SPA teste plan === 'FREE') : un compte
  // payant dont la période payée est dépassée arrive en FREE avec expired=true. Il garde son badge
  // « Expiré » et la carte « repasser en gratuit », mais il est bridé comme un gratuit.
  const isExpired = p.plan === 'FREE' && p.expired === true;
  const isFree = p.plan === 'FREE' && !isExpired;
  const isPaid = p.plan === 'PAID';
  // Impayé en cours (Stripe relance le moyen de paiement) ou abonnement tombé faute de paiement.
  const paymentFailed = p.paymentFailed === true;

  // Le formulaire se lit sur ce qui a déjà été saisi, sinon sur le compte (le plan arrive après le
  // premier rendu, d'où la lecture directe plutôt qu'un préremplissage par effet).
  const billingForm = billing || {
    legalName: p.legalName || '',
    countryCode: p.country || 'FR',
    vatNumber: p.vatNumber || '',
  };
  const updateBilling = (field) => (e) => setBilling({...billingForm, [field]: e.target.value});

  // La formule n'est pas encore chargée (ex. juste après la première connexion) : on affiche un état de
  // chargement plutôt qu'un panneau vide. openSubscription() a déclenché un refetch, ce rendu s'actualise.
  if (!p.plan) {
    return (
      <div className="cc-subscription-panel">
        <div className="text-center text-muted py-4">
          <i className="spinner-border spinner-border-sm me-2"/> <FormattedMessage id="LOADING"/>
        </div>
      </div>
    );
  }

  // Passage en payant : Stripe ne s'ouvre qu'une fois les informations légales connues. Un compte créé
  // en formule gratuite ne les porte pas (l'inscription gratuite ne demande qu'un email et un pays),
  // et sans elles la facture d'abonnement ne serait pas en règle — on les demande donc ici d'abord.
  const upgrade = (interval) => {
    if (!p.billingComplete) {
      setConfirmTarget(null);
      setBillingTarget(interval);
      return;
    }
    startCheckout(interval, {});
  };

  const startCheckout = (interval, billingData) => {
    setLoading(true);
    API.postAdminSubscriptionCheckout(history, {plan: interval, countryCode: p.country, ...billingData}).then(r => {
      setLoading(false);
      if (r && r.data && r.data.success === true) {
        window.location.replace(r.data.data);
      } else if (r && r.data && r.data.data === 'BILLING_INFO_REQUIRED') {
        // Filet de sécurité : le formulaire est déjà obligatoire côté navigateur.
        setBillingTarget(interval);
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    }).catch(() => {
      setLoading(false);
      Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
    });
  };

  const submitBilling = (e) => {
    e.preventDefault();
    startCheckout(billingTarget, billingForm);
  };

  const openStripePortal = () => {
    setLoading(true);
    API.getStripePortalUrl(history).then(r => {
      setLoading(false);
      if (r && r.data && r.data.success === true) {
        window.location.replace(r.data.data);
      } else {
        Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
      }
    }).catch(() => {
      setLoading(false);
      Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
    });
  };

  // Un compte payant provisionné hors du flux Stripe natif (legacy) n'a pas d'abonnement modifiable :
  // on le dit explicitement au lieu du « Une erreur est survenue » générique.
  const notifyFailure = (r) => {
    const noSubscription = r && r.data && r.data.data === Error.subscriptionNotFound();
    if (noSubscription) {
      Utils.notifyWarning(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'SUBSCRIPTION_NOT_FOUND'}));
    } else {
      Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
    }
  };

  // Mensuel ↔ annuel : Stripe met à jour l'abonnement en place, au prorata. Pas de passage par Checkout.
  const switchInterval = (interval) => {
    setLoading(true);
    API.postChangeInterval(history, {plan: interval}).then(r => {
      setLoading(false);
      setConfirmTarget(null);
      if (r && r.data && r.data.success === true) {
        Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'INTERVAL_CHANGED'}));
        p.refresh && p.refresh();
      } else {
        notifyFailure(r);
      }
    }).catch(() => {
      setLoading(false);
      setConfirmTarget(null);
      Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
    });
  };

  // Réactivation : on lève le cancel_at_period_end, l'abonnement repart normalement à l'échéance.
  const resume = () => {
    setLoading(true);
    API.postResumeSubscription(history).then(r => {
      setLoading(false);
      if (r && r.data && r.data.success === true) {
        Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'SUBSCRIPTION_RESUMED'}));
        p.refresh && p.refresh();
      } else {
        notifyFailure(r);
      }
    }).catch(() => {
      setLoading(false);
      Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
    });
  };

  // Rétrogradation : annulation Stripe en fin de période. Le compte reste PAID jusqu'à l'échéance ; c'est
  // le webhook customer.subscription.deleted qui bascule réellement en FREE.
  const downgrade = () => {
    setLoading(true);
    API.postDowngrade(history).then(r => {
      setLoading(false);
      setConfirmTarget(null);
      if (r && r.data && r.data.success === true) {
        Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'DOWNGRADE_SCHEDULED'}));
        p.refresh && p.refresh();
      } else {
        notifyFailure(r);
      }
    }).catch(() => {
      setLoading(false);
      setConfirmTarget(null);
      Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
    });
  };

  // Compte expiré → gratuit : bascule immédiate (aucun abonnement actif à laisser courir). On garde la
  // 1ʳᵉ ressource de chaque type, rien n'est supprimé (réactivé au ré-abonnement). Distinct de downgrade(),
  // qui programme l'annulation Stripe d'un abonnement encore actif.
  const switchToFree = () => {
    setLoading(true);
    API.postSwitchExpiredToFree(history).then(r => {
      setLoading(false);
      setConfirmTarget(null);
      if (r && r.data && r.data.success === true) {
        Utils.notifySuccess(intl.formatMessage({id: 'OK'}), intl.formatMessage({id: 'SWITCHED_TO_FREE'}));
        p.refresh && p.refresh();
      } else {
        notifyFailure(r);
      }
    }).catch(() => {
      setLoading(false);
      setConfirmTarget(null);
      Utils.notifyError(intl.formatMessage({id: 'ERROR'}), intl.formatMessage({id: 'ERROR_OCCURRED'}));
    });
  };

  const isAnnual = p.interval === 'ANNUAL';
  const planLabelId = isExpired ? 'PLAN_EXPIRED' : isFree ? 'PLAN_FREE' : (isAnnual ? 'PLAN_ANNUAL' : 'PLAN_MONTHLY');

  // Rétrogradation programmée : le compte est encore payant (badge « Annuel »), mais il ne sera plus
  // débité et basculera en gratuit à l'échéance. Sans ce bandeau, rien ne le disait au chauffeur.
  const cancelPending = isPaid && p.cancelAtPeriodEnd === true;
  const endDate = p.subscriptionEndAt != null
    ? new Intl.DateTimeFormat(intl.locale || 'fr', {dateStyle: 'long'}).format(new Date(p.subscriptionEndAt))
    : null;

  // Carte générique. `interval` vaut 'FREE' | 'MONTHLY' | 'ANNUAL' ; `ctaId` et `onCta` varient selon
  // qu'on vend un abonnement (compte gratuit/expiré) ou qu'on propose une bascule (compte payant).
  const planCard = ({interval, price, popular, extra, ctaId, ctaVariant = 'primary', onCta}) => {
    const free = interval === 'FREE';
    return (
      <div key={interval} className={`cc-plan-card ${popular ? 'is-popular' : ''}`}>
        {popular && <span className="cc-plan-badge"><FormattedMessage id="PLAN_ANNUAL_TAGLINE"/></span>}
        <div className="cc-plan-name"><FormattedMessage id={`PLAN_${interval}`}/></div>
        <div className="cc-plan-price">
          {price != null ? fmt.format(price) : '—'}
          {!free && (
            <span className="cc-plan-price-suffix"> {intl.formatMessage({id: 'HT_LABEL'})}{intl.formatMessage({id: interval === 'ANNUAL' ? 'PER_YEAR' : 'PER_MONTH'})}</span>
          )}
        </div>
        <div className="cc-plan-extra">{extra}</div>
        <ul className="cc-plan-features">
          {(free ? FREE_FEATURES : PAID_FEATURES).map(f => <li key={f}><FontAwesomeIcon icon={faCheck}/> <FormattedMessage id={f}/></li>)}
        </ul>
        <button type="button" className={`ccv2-btn ccv2-btn-${ctaVariant} cc-plan-cta`} disabled={loading} onClick={onCta}>
          <FormattedMessage id={ctaId}/>
        </button>
      </div>
    );
  };

  const annualExtra = (
    <>
      {annualPerMonth != null && <span className="cc-plan-equiv"><FormattedMessage id="EQUIV_PER_MONTH" values={{price: fmt.format(annualPerMonth)}}/></span>}
      {annualSave != null && annualSave > 0 && <span className="cc-plan-save"><FormattedMessage id="SAVE_AMOUNT" values={{amount: fmt.format(annualSave)}}/></span>}
    </>
  );

  return (
    <div className="cc-subscription-panel">
      {/* Ouverture depuis un plafond de ressource (bouton « Nouveau » verrouillé) : on explique la
          situation en douceur AVANT de dérouler l'offre — les cartes de formule restent juste dessous,
          la vente n'est pas compromise (aucune étape bloquante). */}
      {isFree && limitReason && (
        <div className="cc-sub-notice cc-sub-limit">
          <div className="cc-sub-notice-title">
            <FontAwesomeIcon icon={faCircleInfo}/> <FormattedMessage id="PLAN_LIMIT_TITLE"/>
          </div>
          <p><FormattedMessage id="PLAN_LIMIT_BODY"/></p>
        </div>
      )}

      <div className="cc-sub-current">
        <span className="cc-sub-current-label"><FormattedMessage id="YOUR_CURRENT_PLAN"/></span>
        <span className={`ccv2-sub-badge ${isPaid ? 'is-paid' : 'is-free'}`}>
          <FontAwesomeIcon icon={faWallet}/> <FormattedMessage id={planLabelId}/>
          {cancelPending && <span className="cc-sub-badge-suffix"> · <FormattedMessage id="DOWNGRADE_PENDING_BADGE"/></span>}
        </span>
      </div>

      {/* Impayé : tant que le compte est encore payant, on dit jusqu'à quand et on mène au portail
          Stripe (moyen de paiement) ; une fois basculé en gratuit, on explique et les cartes suivent. */}
      {paymentFailed && (
        <div className="cc-sub-notice cc-sub-limit">
          <div className="cc-sub-notice-title">
            <FontAwesomeIcon icon={faTriangleExclamation}/> <FormattedMessage id="PAYMENT_FAILED_TITLE"/>
          </div>
          <p>
            {isPaid && endDate
              ? <FormattedMessage id="PAYMENT_FAILED_BODY" values={{date: <strong>{endDate}</strong>}}/>
              : <FormattedMessage id="PAYMENT_FAILED_EXPIRED_BODY"/>}
          </p>
          {isPaid && (
            <button type="button" className="ccv2-btn ccv2-btn-primary" disabled={loading} onClick={openStripePortal}>
              <FontAwesomeIcon icon={faCreditCard}/> <FormattedMessage id="UPDATE_PAYMENT_METHOD"/>
            </button>
          )}
        </div>
      )}

      {(isFree || isExpired) && p.bookingQuota != null && (
        <div className="cc-sub-quota">
          <FormattedMessage id="BOOKINGS_THIS_MONTH"/>&nbsp;:&nbsp;
          <strong>{p.bookingsUsedThisMonth || 0} / {p.bookingQuota}</strong>
        </div>
      )}

      {/* Compte gratuit / expiré : on vend les deux formules payantes. Un compte expiré peut en plus
          basculer explicitement en formule gratuite (il n'a plus d'abonnement actif) — un compte déjà
          gratuit n'a évidemment pas cette carte. */}
      {(isFree || isExpired) && (
        <div className={`cc-pricing ${isExpired ? 'cc-pricing-3' : ''}`}>
          {isExpired && planCard({
            interval: 'FREE', price: 0,
            extra: <span className="cc-plan-tagline"><FormattedMessage id="PLAN_FREE_TAGLINE"/></span>,
            ctaId: 'SWITCH_TO_FREE', ctaVariant: 'secondary',
            onCta: () => setConfirmTarget('FREE'),
          })}
          {planCard({interval: 'MONTHLY', price: monthly, ctaId: 'CHOOSE', onCta: () => upgrade('MONTHLY')})}
          {planCard({interval: 'ANNUAL', price: annual, popular: true, extra: annualExtra,
                     ctaId: 'CHOOSE', onCta: () => upgrade('ANNUAL')})}
        </div>
      )}

      {/* Informations légales exigées avant tout passage en payant : la facture d'abonnement doit porter
          la raison sociale du client, et le taux de TVA est figé avant l'ouverture de la session. Elles
          sont enregistrées sur le compte dès l'envoi, donc un abandon du paiement ne les fait pas
          perdre. L'adresse est demandée par Stripe à l'écran suivant. */}
      {billingTarget && (
        <form className="cc-sub-confirm" ref={billingRef} onSubmit={submitBilling}>
          <p><FormattedMessage id="BILLING_INFO_INTRO"/></p>
          <div className="mb-3">
            <label className="form-label" htmlFor="cc-legal-name"><FormattedMessage id="LEGAL_NAME"/></label>
            <input className="form-control" id="cc-legal-name" type="text" required
                   value={billingForm.legalName} onChange={updateBilling('legalName')}/>
            <small className="text-muted"><FormattedMessage id="LEGAL_NAME_HELP"/></small>
          </div>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="countryCode"><FormattedMessage id="COUNTRY"/></label>
              <Countries id="countryCode" value={billingForm.countryCode} onChange={updateBilling('countryCode')} intl={intl}/>
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label" htmlFor="cc-vat-number"><FormattedMessage id="VAT_NUMBER"/></label>
              <input className="form-control" id="cc-vat-number" type="text"
                     value={billingForm.vatNumber} onChange={updateBilling('vatNumber')}/>
              <small className="text-muted"><FormattedMessage id="VAT_NUMBER_HELP"/></small>
            </div>
          </div>
          <div className="cc-sub-actions">
            <button type="submit" className="ccv2-btn ccv2-btn-primary" disabled={loading}>
              {loading && <i className="spinner-border spinner-border-sm text-light me-2"/>}
              <FormattedMessage id="CONTINUE_TO_PAYMENT"/>
            </button>
            <button type="button" className="cc-sub-downgrade" disabled={loading} onClick={() => setBillingTarget(null)}>
              <FormattedMessage id="CANCEL"/>
            </button>
          </div>
        </form>
      )}

      {/* Confirmation de la bascule expiré → gratuit : on rassure sur le sort des ressources existantes
          (seule la 1ʳᵉ de chaque type reste active, rien n'est supprimé) avant d'agir. */}
      {isExpired && confirmTarget === 'FREE' && (
        <div className="cc-sub-confirm" ref={confirmRef}>
          <p><FormattedMessage id="SWITCH_TO_FREE_CONFIRM"/></p>
          <div className="cc-sub-actions">
            <button type="button" className="ccv2-btn ccv2-btn-primary" disabled={loading} onClick={switchToFree}>
              <FormattedMessage id="CONFIRM"/>
            </button>
            <button type="button" className="cc-sub-downgrade" disabled={loading} onClick={() => setConfirmTarget(null)}>
              <FormattedMessage id="CANCEL"/>
            </button>
          </div>
        </div>
      )}

      {/* Rétrogradation programmée : on remplace les cartes de bascule par un bandeau rassurant. Laisser
          la bascule d'intervalle serait piégeur — côté Stripe elle lève le cancel_at_period_end, donc elle
          réactiverait l'abonnement sans le dire. La seule action offerte est la réactivation explicite. */}
      {cancelPending && (
        <div className="cc-sub-notice">
          <div className="cc-sub-notice-title">
            <FontAwesomeIcon icon={faCircleInfo}/> <FormattedMessage id="DOWNGRADE_PENDING_TITLE"/>
          </div>
          <p>
            {endDate
              ? <FormattedMessage id="DOWNGRADE_PENDING_BODY" values={{date: <strong>{endDate}</strong>}}/>
              : <FormattedMessage id="DOWNGRADE_PENDING_BODY_NO_DATE"/>}
          </p>
          <button type="button" className="ccv2-btn ccv2-btn-primary" disabled={loading} onClick={resume}>
            <FormattedMessage id="RESUME_SUBSCRIPTION"/>
          </button>
        </div>
      )}

      {/* Compte payant : mêmes cartes, pour que le chauffeur voie ce qu'il perdrait en repassant au
          gratuit. On masque la carte de la formule qu'il a déjà — il ne reste que les bascules possibles. */}
      {isPaid && !cancelPending && (
        <>
          <div className="cc-pricing">
            {/* Aucune de ces cartes n'agit directement : elles arment la confirmation ci-dessous. */}
            {planCard({
              interval: 'FREE', price: 0,
              extra: <span className="cc-plan-tagline"><FormattedMessage id="PLAN_FREE_TAGLINE"/></span>,
              ctaId: 'DOWNGRADE_TO_FREE', ctaVariant: 'secondary',
              onCta: () => setConfirmTarget('FREE'),
            })}
            {isAnnual
              ? planCard({interval: 'MONTHLY', price: monthly, ctaId: 'SWITCH_TO_MONTHLY',
                          onCta: () => setConfirmTarget('MONTHLY')})
              : planCard({interval: 'ANNUAL', price: annual, popular: true, extra: annualExtra,
                          ctaId: 'SWITCH_TO_ANNUAL', onCta: () => setConfirmTarget('ANNUAL')})}
          </div>

          {confirmTarget && (
            <div className="cc-sub-confirm" ref={confirmRef}>
              <p>
                <FormattedMessage id={confirmTarget === 'FREE' ? 'DOWNGRADE_CONFIRM'
                  : confirmTarget === 'ANNUAL' ? 'SWITCH_TO_ANNUAL_CONFIRM' : 'SWITCH_TO_MONTHLY_CONFIRM'}/>
              </p>
              <div className="cc-sub-actions">
                <button type="button" className={`ccv2-btn ${confirmTarget === 'FREE' ? 'ccv2-btn-danger' : 'ccv2-btn-primary'}`}
                        disabled={loading}
                        onClick={() => (confirmTarget === 'FREE' ? downgrade() : switchInterval(confirmTarget))}>
                  <FormattedMessage id="CONFIRM"/>
                </button>
                <button type="button" className="cc-sub-downgrade" disabled={loading} onClick={() => setConfirmTarget(null)}>
                  <FormattedMessage id="CANCEL"/>
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Le portail Stripe reste le point d'entrée pour le moyen de paiement et les factures, y compris
          quand une annulation est programmée. */}
      {isPaid && (
        <div className="cc-sub-manage">
          <button type="button" className="ccv2-btn ccv2-btn-secondary" disabled={loading} onClick={openStripePortal}>
            <FontAwesomeIcon icon={faCreditCard}/> <FormattedMessage id="MANAGE_SUBSCRIPTION"/>
          </button>
        </div>
      )}
    </div>
  );
};
